import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Plus, Save, Trash2, Upload } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { INITIAL_MOCK_ITEMS } from '@/modules/inventory/items/types'
import type { InventoryTransferRow, TransferLine } from '../types'
import { balanceQuantity, newTransferLine } from '../types'

const MOCK_WAREHOUSES = [
  { value: 'w1', label: 'Warehouse 1' },
  { value: 'w2', label: 'Warehouse 2' },
]
const MOCK_BATCHES = [
  { value: 'b1', label: 'Batch A' },
  { value: 'b2', label: 'Batch B' },
  { value: 'b3', label: 'Batch C' },
]

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

export const InventoryTransferEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const stateTr = (location.state as { transfer?: InventoryTransferRow } | null)?.transfer
  const isNew = !id || id === 'new'
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [transferDate, setTransferDate] = useState(todayIso())
  const [remarks, setRemarks] = useState('')
  const [sourceWarehouseId, setSourceWarehouseId] = useState('')
  const [destinationWarehouseId, setDestinationWarehouseId] = useState('')
  const [attachment, setAttachment] = useState<File | null>(null)

  const [lines, setLines] = useState<TransferLine[]>(() =>
    Array.from({ length: 5 }, () => newTransferLine())
  )

  const itemOptions = useMemo(
    () =>
      INITIAL_MOCK_ITEMS.map((i) => ({
        value: String(i.id),
        label: `${i.code} — ${i.name}`,
      })),
    []
  )

  useEffect(() => {
    if (!stateTr || isNew) return
    setSourceWarehouseId(
      stateTr.sourceWarehouse === 'Warehouse 1' ? 'w1' : stateTr.sourceWarehouse === 'Warehouse 2' ? 'w2' : ''
    )
    setDestinationWarehouseId(
      stateTr.destinationWarehouse === 'Warehouse 1'
        ? 'w1'
        : stateTr.destinationWarehouse === 'Warehouse 2'
          ? 'w2'
          : ''
    )
  }, [stateTr, isNew])

  const handleBack = () => navigate('/inventory/transfers')

  const addLines = (n: number) => {
    setLines((prev) => [...prev, ...Array.from({ length: n }, () => newTransferLine())])
  }

  const updateLine = (index: number, patch: Partial<TransferLine>) => {
    setLines((prev) => {
      const next = [...prev]
      const prevItemId = next[index].itemId
      const line = { ...next[index], ...patch }
      if (patch.itemId !== undefined && patch.itemId !== prevItemId) {
        line.currentQty = 10
        line.quantityTransfer = 0
      }
      next[index] = line
      return next
    })
  }

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!sourceWarehouseId || !destinationWarehouseId) {
      toast.error(t('inventoryTransfers.validationWarehouses'))
      return
    }
    if (sourceWarehouseId === destinationWarehouseId) {
      toast.error(t('inventoryTransfers.validationDifferentWarehouses'))
      return
    }
    toast.success(t('inventoryTransfers.saveStub'))
    navigate('/inventory/transfers')
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryTransfers.formTitle')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryTransfers.formSubtitle')}</p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={handleBack} className="w-full sm:w-auto">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back')}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('inventoryTransfers.sectionTransferDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="tr-date">{t('inventoryTransfers.transferDate')}</Label>
              <Input
                id="tr-date"
                type="date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="tr-remarks">{t('inventoryTransfers.remarks')}</Label>
              <Input id="tr-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('inventoryTransfers.file')}</Label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => setAttachment(e.target.files?.[0] ?? null)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {t('inventoryTransfers.browse')}
                </Button>
                <span className="truncate text-sm text-muted-foreground">
                  {attachment?.name ?? t('inventoryTransfers.noFileChosen')}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                {t('inventoryTransfers.sourceWarehouse')} <span className="text-destructive">*</span>
              </Label>
              <Select value={sourceWarehouseId || undefined} onValueChange={setSourceWarehouseId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryTransfers.selectSource')} />
                </SelectTrigger>
                <SelectContent>
                  {MOCK_WAREHOUSES.map((w) => (
                    <SelectItem key={w.value} value={w.value}>
                      {w.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                {t('inventoryTransfers.destinationWarehouse')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Select value={destinationWarehouseId || undefined} onValueChange={setDestinationWarehouseId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryTransfers.selectDestination')} />
                </SelectTrigger>
                <SelectContent>
                  {MOCK_WAREHOUSES.map((w) => (
                    <SelectItem key={w.value} value={w.value}>
                      {w.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="hidden md:block" aria-hidden />
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">{t('inventoryTransfers.sectionTransferredItems')}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)}>
                <Plus className="mr-1 h-4 w-4" />
                {t('inventoryTransfers.addRow')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)}>
                {t('inventoryTransfers.add3Rows')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)}>
                {t('inventoryTransfers.add5Rows')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className={tableHeaderClass}>{t('inventoryTransfers.colItem')}</TableHead>
                    <TableHead className={tableHeaderClass}>{t('inventoryTransfers.colBatch')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryTransfers.colCurrentQty')}
                    </TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryTransfers.colQtyTransfer')}
                    </TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryTransfers.colBalanceQty')}
                    </TableHead>
                    <TableHead className={`${tableHeaderClass} w-[64px] text-center`}>
                      {t('common.delete')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow key={line.id}>
                      <TableCell className="align-top py-2">
                        <Select
                          value={line.itemId || undefined}
                          onValueChange={(v) => updateLine(index, { itemId: v })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('inventoryTransfers.selectItem')} />
                          </SelectTrigger>
                          <SelectContent>
                            {itemOptions.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top py-2">
                        <Select
                          value={line.batchId || undefined}
                          onValueChange={(v) => updateLine(index, { batchId: v })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('inventoryTransfers.selectBatch')} />
                          </SelectTrigger>
                          <SelectContent>
                            {MOCK_BATCHES.map((b) => (
                              <SelectItem key={b.value} value={b.value}>
                                {b.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          readOnly
                          className="h-9 bg-muted/50 text-center"
                          value={line.itemId ? String(line.currentQty) : ''}
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          type="number"
                          className="h-9 text-center"
                          min={0}
                          value={line.quantityTransfer || ''}
                          onChange={(e) =>
                            updateLine(index, { quantityTransfer: Number(e.target.value) || 0 })
                          }
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          readOnly
                          className="h-9 bg-muted/50 text-center"
                          value={line.itemId ? String(balanceQuantity(line)) : ''}
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => removeLine(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit">
            <Save className="mr-2 h-4 w-4" />
            {t('inventoryTransfers.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}
